import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useHealthCheck } from '../hooks/useHealthCheck';
import YearSelector from './YearSelector';
import NotificationDropdown from './NotificationDropdown';
import api from '../services/api';
import { getUploadUrl } from '../utils/getUploadUrl';
import {
  FiHome,
  FiUsers,
  FiBookOpen,
  FiUserCheck,
  FiDollarSign,
  FiFileText,
  FiMenu,
  FiX,
  FiLogOut,
  FiUser,
  FiSettings,
  FiChevronDown,
  FiBell,
  FiSun,
  FiMoon,
  FiCircle
} from 'react-icons/fi';

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openSection, setOpenSection] = useState('main'); // 'main', 'vieScolaire', 'admin', 'settings', etc.
  const [openSubSection, setOpenSubSection] = useState(null); // 'profs', 'parents', 'classesMatieres', 'evaluations', null
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [etablissement, setEtablissement] = useState(null);
  const location = useLocation();
  const { logout, user, hasPermission } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { isOperational } = useHealthCheck(30000); // Vérification toutes les 30 secondes

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };
  const handleSubSection = (sub) => {
    setOpenSubSection(openSubSection === sub ? null : sub);
  };

  // Fermer le dropdown du profil quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownOpen && !event.target.closest('.profile-dropdown-container')) {
        setProfileDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileDropdownOpen]);

  // Charger les données de l'établissement
  useEffect(() => {
    const fetchEtablissement = async () => {
      // Vérifier que l'utilisateur est connecté et qu'un token existe
      const token = localStorage.getItem('token');
      if (!user || !token) {
        setEtablissement(null);
        return;
      }

      try {
        // Utiliser AbortController pour éviter les erreurs si le composant est démonté
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // Timeout de 10 secondes
        
        const res = await api.get('/etablissement', {
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        // L'API peut retourner un tableau ou un objet unique
        if (Array.isArray(res.data.data)) {
          setEtablissement(res.data.data[0] || null);
        } else if (res.data.data) {
          setEtablissement(res.data.data);
        } else {
          setEtablissement(null);
        }
      } catch (error) {
        // Ignorer les erreurs silencieusement pour ne pas bloquer l'application
        // L'erreur peut être due à une extension Chrome, un timeout, ou un problème réseau
        if (error.name !== 'AbortError' && error.response?.status !== 401) {
          // Ne logger que les erreurs non prévues
          if (process.env.NODE_ENV === 'development') {
            console.warn("Impossible de charger les données de l'établissement:", error.message);
          }
        }
        setEtablissement(null);
      }
    };
    
    // Utiliser un petit délai pour s'assurer que l'authentification est complète
    const timeoutId = setTimeout(() => {
      fetchEtablissement();
    }, 100);
    
    return () => {
      clearTimeout(timeoutId);
    };
  }, [user]);


  const mainNavigation = [
    { name: 'Tableau de bord', href: '/app/dashboard', icon: FiHome, permission: null },
    { name: 'Ouverture de classe', href: '/app/ouvertures-classes', icon: FiBookOpen, permission: 'ouvertures-classes.view' },
    { name: 'Paiements', href: '/app/paiements', icon: FiDollarSign, permission: 'paiements.view' },
    { name: 'Absences', href: '/app/absences', icon: FiFileText, permission: 'absences.view' },
    { name: 'Relevés de notes', href: '/app/releves-notes', icon: FiFileText, permission: 'releves-notes.view' },
  ];

  const vieScolaireNavigation = [
    { name: 'Années scolaires', href: '/app/annees-scolaires', icon: FiHome, permission: 'annees-scolaires.view' },
    { name: 'Élèves', href: '/app/eleves', icon: FiUsers, permission: 'eleves.view' },
    { name: 'Parents', href: '/app/parents', icon: FiUsers, permission: 'eleves.view' },
    { name: 'Inscriptions', href: '/app/inscriptions', icon: FiUserCheck, permission: 'inscriptions.view' },
    { name: 'Evaluations', href: '/app/evaluations', icon: FiFileText, permission: 'evaluations.view' },
  ];
  
  const adminNavigation = [
    { name: 'Utilisateurs', href: '/app/users', icon: FiSettings, permission: 'users.view' },
    { name: 'Rôles & Permissions', href: '/app/roles-permissions', icon: FiSettings, permission: 'roles.view' },
    { name: 'Notifications', href: '/app/notifications-admin', icon: FiBell, permission: 'notifications.admin' },
  ];
  
  const profsNavigation = [
    { name: 'Professeurs', href: '/app/professeurs', icon: FiUsers, permission: 'professeurs.view' },
    { name: 'Niveaux de qualification', href: '/app/niveau-qualif-prof', icon: FiFileText, permission: 'niveau-qualif-prof.view' },
    { name: 'Qualifications', href: '/app/qualifications', icon: FiFileText, permission: 'qualifications.view' },
    { name: 'Qualifications professeurs', href: '/app/prof-qualif', icon: FiFileText, permission: 'prof-qualif.view' },
  ];

  const parentsNavigation = [
    { name: 'Types de parenté', href: '/app/types-parentes', icon: FiSettings, permission: 'types-parentes.view' },
    { name: 'Religions', href: '/app/religions', icon: FiFileText, permission: 'religions.view' },
    { name: 'Professions', href: '/app/professions', icon: FiFileText, permission: 'professions.view' },
  ];

  const classesMatieresNavigation = [
    { name: 'Classes', href: '/app/classes', icon: FiBookOpen, permission: 'classes.view' },
    { name: 'Cycles', href: '/app/cycles', icon: FiBookOpen, permission: 'settings.view' },
    { name: 'Périodes', href: '/app/periodes', icon: FiFileText, permission: 'periodes.view' },
    { name: 'Branches', href: '/app/branches', icon: FiBookOpen, permission: 'branches.view' },
    { name: 'Matières', href: '/app/matieres', icon: FiBookOpen, permission: 'matieres.view' },
    { name: 'Classe-matières', href: '/app/classe-matieres', icon: FiBookOpen, permission: 'classe-matieres.view' },
  ];

  const evaluationsNavigation = [
    { name: 'Types d\'évaluations', href: '/app/type-evaluations', icon: FiFileText, permission: 'type-evaluations.view' },
  ];

  const settingsNavigation = [
    { name: 'Etablissement', href: '/app/etablissement', icon: FiSettings, permission: 'etablissement.view' },
    { name: 'Diocèses', href: '/app/dioceses', icon: FiFileText, permission: 'dioceses.view' },
    { name: 'Paroisses', href: '/app/paroisses', icon: FiFileText, permission: 'paroisses.view' },
    { name: 'Incidents', href: '/app/incidents', icon: FiFileText, permission: 'incidents.view' },
  ];

  const isActive = (path) => location.pathname === path;

  // Filtrage des menus selon le rôle
  const filteredMainNavigation = mainNavigation.filter(item => {
    // Pour les enseignants, ne montrer que "Ouverture de classe"
    if (user?.role === 'enseignant') {
      return item.name === 'Ouverture de classe';
    }
    // Pour les autres rôles, utiliser les permissions
    return !item.permission || hasPermission(item.permission);
  });

  // Filtrage des menus de vie scolaire
  const filteredVieScolaireNavigation = vieScolaireNavigation.filter(item => {
    // Les enseignants ne voient aucun menu de vie scolaire
    if (user?.role === 'enseignant') {
      return false;
    }
    return !item.permission || hasPermission(item.permission);
  });

  // Filtrage des menus d'administration
  const filteredAdminNavigation = adminNavigation.filter(item => {
    // Les enseignants ne voient aucun menu d'administration
    if (user?.role === 'enseignant') {
      return false;
    }
    return !item.permission || hasPermission(item.permission);
  });

  // Filtrage des menus de professeurs
  const filteredProfsNavigation = profsNavigation.filter(item => {
    // Les enseignants ne voient aucun menu de professeurs
    if (user?.role === 'enseignant') {
      return false;
    }
    return !item.permission || hasPermission(item.permission);
  });

  // Filtrage des menus de parents
  const filteredParentsNavigation = parentsNavigation.filter(item => {
    // Les enseignants ne voient aucun menu de parents
    if (user?.role === 'enseignant') {
      return false;
    }
    return !item.permission || hasPermission(item.permission);
  });

  // Filtrage des menus de classes et matières
  const filteredClassesMatieresNavigation = classesMatieresNavigation.filter(item => {
    // Les enseignants ne voient aucun menu de classes et matières
    if (user?.role === 'enseignant') {
      return false;
    }
    return !item.permission || hasPermission(item.permission);
  });

  // Filtrage des menus d'évaluations
  const filteredEvaluationsNavigation = evaluationsNavigation.filter(item => {
    // Les enseignants ne voient aucun menu d'évaluations
    if (user?.role === 'enseignant') {
      return false;
    }
    return !item.permission || hasPermission(item.permission);
  });

  // Filtrage des menus de paramètres
  const filteredSettingsNavigation = settingsNavigation.filter(item => {
    // Les enseignants ne voient aucun menu de paramètres
    if (user?.role === 'enseignant') {
      return false;
    }
    return !item.permission || hasPermission(item.permission);
  });

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'block' : 'hidden'} md:block md:w-110 bg-gradient-to-b from-primary-500 via-primary-800 to-primary-700 shadow-2xl rounded-r-3xl border-r-2 border-primary-200 z-30`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-6 border-b border-primary-600">
            <div className="flex items-center space-x-3">
              <div className="bg-white rounded-full p-2 shadow-md">
                <FiBookOpen className="h-7 w-7 text-primary-700" />
              </div>
              <span className="text-2xl font-extrabold tracking-wide text-white drop-shadow">Schola v3</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="md:hidden"
            >
              <FiX className="h-6 w-6" />
            </button>
          </div>
          
          <nav className="flex-1 p-4">
            {/* Main navigation section (déroulante) */}
            <div>
              <button
                className="flex items-center justify-start w-full px-3 py-2 text-lg font-bold text-blue-100 uppercase tracking-wider hover:bg-gray-100 hover:text-black rounded transition"
                onClick={() => toggleSection('main')}
              >
                <FiHome className="h-5 w-5 mr-1" />
                <span className="flex-1 text-left">Accueil</span>
                <FiChevronDown className={`transition-transform ${openSection === 'main' ? 'rotate-180' : ''}`} />
              </button>
              {openSection === 'main' && (
                <ul className="space-y-2 mt-1">
                  {filteredMainNavigation.map((item) => (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        className={`flex items-center space-x-3 p-3 rounded-lg transition-colors text-sm ${
                          isActive(item.href)
                            ? 'bg-white/10 text-white font-bold'
                            : 'text-primary-100 hover:bg-white/10 hover:text-black'
                        }`}
                      >
                        <item.icon className="h-5 w-5" />
                        <span>{item.name}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {/* Vie scolaire section (déroulante) */}
            {filteredVieScolaireNavigation.length > 0 && (
              <div>
                <button
                  className="flex items-center justify-start w-full px-3 py-2 text-lg font-bold text-blue-100 uppercase tracking-wider hover:bg-gray-100 hover:text-black rounded transition mt-8"
                  onClick={() => toggleSection('vieScolaire')}
                >
                  <FiBookOpen className="h-5 w-5 mr-1" />
                  <span className="flex-1 text-left">Vie scolaire</span>
                  <FiChevronDown className={`transition-transform ${openSection === 'vieScolaire' ? 'rotate-180' : ''}`} />
                </button>
                {openSection === 'vieScolaire' && (
                  <ul className="space-y-2 mt-1">
                    {filteredVieScolaireNavigation.map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          className={`flex items-center space-x-3 p-3 rounded-lg transition-colors text-sm ${
                            isActive(item.href)
                              ? 'bg-white/10 text-white font-bold'
                              : 'text-primary-100 hover:bg-white/10 hover:text-black'
                          }`}
                        >
                          <item.icon className="h-5 w-5" />
                          <span>{item.name}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {/* Administration section (déroulante) */}
            {filteredAdminNavigation.length > 0 && (
              <div>
                <button
                  className="flex items-center justify-start w-full px-3 py-2 text-lg font-bold text-blue-100 uppercase tracking-wider hover:bg-gray-100 rounded transition mt-8 hover:text-black"
                  onClick={() => toggleSection('admin')}
                >
                  <FiSettings className="h-5 w-5 mr-1" />
                  <span className="flex-1 text-left">Administration</span>
                  <FiChevronDown className={`transition-transform ${openSection === 'admin' ? 'rotate-180' : ''}`} />
                </button>
                {openSection === 'admin' && (
                  <ul className="space-y-2 mt-1">
                    {filteredAdminNavigation.map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          className={`flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                            isActive(item.href)
                              ? 'bg-white/10 text-white font-bold'
                              : 'text-primary-100 hover:bg-white/10 hover:text-black'
                          }`}
                        >
                          <item.icon className="h-5 w-5" />
                          <span>{item.name}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {/* Paramètres section (déroulante) */}
            {(filteredProfsNavigation.length > 0 || filteredParentsNavigation.length > 0 || filteredClassesMatieresNavigation.length > 0 || filteredEvaluationsNavigation.length > 0 || filteredSettingsNavigation.length > 0) && (
              <div>
                <button
                  className="flex items-center justify-start w-full px-3 py-2 text-lg font-bold text-blue-100 uppercase tracking-wider hover:bg-gray-100 rounded transition mt-8 hover:text-black"
                  onClick={() => toggleSection('settings')}
                >
                  <FiFileText className="h-5 w-5 mr-1" />
                  <span className="flex-1 text-left">Paramètres</span>
                  <FiChevronDown className={`transition-transform ${openSection === 'settings' ? 'rotate-180' : ''}`} />
                </button>
                {openSection === 'settings' && (
                  <>
                    {/* Sous-section PROFS */}
                    {filteredProfsNavigation.length > 0 && (
                      <button
                        className="flex items-center justify-start w-full px-3 py-2 text-base font-semibold text-primary-200 uppercase tracking-wider hover:bg-gray-100 rounded transition mb-2 hover:text-black ml-4"
                        onClick={() => handleSubSection('profs')}
                      >
                        <FiUsers className="h-5 w-5 mr-1" />
                        <span className="flex-1 text-left">PROFS</span>
                        <FiChevronDown className={`transition-transform ${openSubSection === 'profs' ? 'rotate-180' : ''}`} />
                      </button>
                    )}
                    {openSubSection === 'profs' && filteredProfsNavigation.length > 0 && (
                      <ul className="space-y-2 ml-4">
                        {filteredProfsNavigation.map((item) => (
                          <li key={item.name}>
                            <Link
                              to={item.href}
                              className={`flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                              isActive(item.href)
                              ? 'bg-white/10 text-white font-bold'
                              : 'text-primary-100 hover:bg-white/10 hover:text-black'
                              }`}
                            >
                              <item.icon className="h-5 w-5" />
                              <span>{item.name}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                    {/* Sous-section Parents */}
                    {filteredParentsNavigation.length > 0 && (
                      <button
                        className="flex items-center justify-start w-full px-3 py-2 text-base font-semibold text-primary-200 uppercase tracking-wider hover:bg-gray-100 rounded transition mb-2 hover:text-black ml-4"
                        onClick={() => handleSubSection('parents')}
                      >
                        <FiUser className="h-5 w-5 mr-1" />
                        <span className="flex-1 text-left">Parents</span>
                        <FiChevronDown className={`transition-transform ${openSubSection === 'parents' ? 'rotate-180' : ''}`} />
                      </button>
                    )}
                    {openSubSection === 'parents' && filteredParentsNavigation.length > 0 && (
                      <ul className="space-y-2 ml-4">
                        {filteredParentsNavigation.map((item) => (
                          <li key={item.name}>
                            <Link
                              to={item.href}
                              className={`flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                              isActive(item.href)
                              ? 'bg-white/10 text-white font-bold'
                              : 'text-primary-100 hover:bg-white/10 hover:text-black'
                              }`}
                            >
                              <item.icon className="h-5 w-5" />
                              <span>{item.name}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                    {/* Sous-section CLASSES ET MATIERES */}
                    {filteredClassesMatieresNavigation.length > 0 && (
                      <button
                        className="flex items-center justify-start w-full px-3 py-2 text-base font-semibold hover:text-black text-primary-200 uppercase tracking-wider hover:bg-gray-100 rounded transition mb-2 ml-4"
                        onClick={() => handleSubSection('classesMatieres')}
                      >
                        <FiBookOpen className="h-5 w-5 mr-1" />
                        <span className="flex-1 text-left">CLASSES ET MATIERES</span>
                        <FiChevronDown className={`transition-transform ${openSubSection === 'classesMatieres' ? 'rotate-180' : ''}`} />
                      </button>
                    )}
                    {openSubSection === 'classesMatieres' && filteredClassesMatieresNavigation.length > 0 && (
                      <ul className="space-y-2 ml-4">
                        {filteredClassesMatieresNavigation.map((item) => (
                          <li key={item.name}>
                            <Link
                              to={item.href}
                              className={`flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                                isActive(item.href)
                                  ? 'bg-white/10 text-white font-bold'
                                  : 'text-primary-100 hover:bg-white/10 hover:text-black'
                              }`}
                            >
                              <item.icon className="h-5 w-5" />
                              <span>{item.name}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                    {/* Sous-section Evaluations */}
                    {filteredEvaluationsNavigation.length > 0 && (
                      <button
                        className="flex items-center justify-start w-full px-3 py-2 text-base font-semibold text-primary-200 uppercase tracking-wider hover:bg-gray-100 rounded transition mb-2 hover:text-black ml-4"
                        onClick={() => handleSubSection('evaluations')}
                      >
                        <FiFileText className="h-5 w-5 mr-1" />
                        <span className="flex-1 text-left">Evaluations</span>
                        <FiChevronDown className={`transition-transform ${openSubSection === 'evaluations' ? 'rotate-180' : ''}`} />
                      </button>
                    )}
                    {openSubSection === 'evaluations' && filteredEvaluationsNavigation.length > 0 && (
                      <ul className="space-y-2 ml-4">
                        {filteredEvaluationsNavigation.map((item) => (
                          <li key={item.name}>
                            <Link
                              to={item.href}
                              className={`flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                                isActive(item.href)
                                  ? 'bg-white/10 text-white font-bold'
                                  : 'text-primary-100 hover:bg-white/10  hover:text-black'
                              }`}
                            >
                              <item.icon className="h-5 w-5" />
                              <span>{item.name}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                    {/* Autres paramètres */}
                    {filteredSettingsNavigation.length > 0 && (
                      <ul className="space-y-2 mt-1">
                        {filteredSettingsNavigation.map((item) => (
                          <li key={item.name}>
                            <Link
                              to={item.href}
                              className={`flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                                isActive(item.href)
                                  ? 'bg-white/10 text-white font-bold'
                                  : 'text-primary-100 hover:bg-white/10  hover:text-black'
                              }`}
                            >
                              <item.icon className="h-5 w-5" />
                              <span>{item.name}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </div>
            )}
          </nav>

          <div className="p-4 border-t">
            <div className="flex items-center justify-center gap-2 p-3 text-primary-100 text-sm font-medium">
              <div className="flex items-center gap-2">
                <FiCircle 
                  className={`h-3 w-3 ${isOperational ? 'text-green-400' : 'text-red-400'}`}
                  style={{ fill: isOperational ? '#4ade80' : '#f87171' }}
                />
                <span>Schola v3 - Système {isOperational ? 'opérationnel' : 'indisponible'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white dark:bg-gray-800 shadow-sm relative z-20">
          <div className="flex items-center p-4">
            {/* Bouton menu mobile - Gauche */}
            <div className="md:hidden mr-4">
              <button
                onClick={() => setSidebarOpen(true)}
              >
                <FiMenu className="h-6 w-6" />
              </button>
            </div>
            
            {/* YearSelector - Gauche */}
            <div className="hidden md:block mr-4">
              <YearSelector />
            </div>
            
            {/* Logo et nom de l'établissement - Centré */}
            <div className="flex-1 flex items-center justify-center gap-4">
              {etablissement?.logo && (
                <img 
                  src={getUploadUrl(etablissement.logo)}
                  alt="Logo de l'établissement"
                  className="h-12 w-12 object-contain rounded-full border-2 border-gray-200 dark:border-gray-700"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              {etablissement?.denomination && (
                <h1 className="text-xl font-bold text-gray-800 dark:text-gray-200 text-center">
                  {etablissement.denomination}
                </h1>
              )}
            </div>
            
            {/* Boutons de droite - Thème, Profil, Notifications */}
            <div className="flex items-center space-x-4">
              {/* Toggle Theme Button */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                aria-label="Toggle theme"
                title={theme === 'light' ? 'Passer au thème sombre' : 'Passer au thème clair'}
              >
                {theme === 'light' ? (
                  <FiMoon className="h-5 w-5" />
                ) : (
                  <FiSun className="h-5 w-5" />
                )}
              </button>
              <div className="relative profile-dropdown-container">
                <button
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  className="flex items-center space-x-2 text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400"
                >
                  <FiUser className="h-5 w-5" />
                  <span>{user?.nom} {user?.prenom}</span>
                  <span className="text-xs bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded">{user?.role}</span>
                  <FiChevronDown className={`h-4 w-4 transition-transform ${profileDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {profileDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-200 dark:border-gray-700">
                    <Link
                      to="/app/profile"
                      onClick={() => setProfileDropdownOpen(false)}
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <FiUser className="h-4 w-4" />
                      <span>Profil</span>
                    </Link>
                    <button
                      onClick={() => {
                        setProfileDropdownOpen(false);
                        logout();
                      }}
                      className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <FiLogOut className="h-4 w-4" />
                      <span>Déconnexion</span>
                    </button>
                  </div>
                )}
              </div>
              <div className="h-6 w-px bg-gray-300 dark:bg-gray-600"></div>
              <NotificationDropdown />
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 bg-gray-100 dark:bg-gray-900">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;